<template>
  <v-card class="mb-4 pa-2 card-container">
    <v-card-title class="pt-4 card-title">
      <v-container class="pa-0 justify-space-between pr-5" fluid fill-height>
        <span class="pa-1 title">Hora de maior acessos</span>
        <chart-type-toggle-button :type.sync="chartType" />
      </v-container>
    </v-card-title>
    <mf-loading loader="faturamento" loader-type="chart">
      <apex-chart v-if="series[0].data.length > 0" class="pr-5 pl-5" type="bar" height="350" :options="chartOptions" :series="series"></apex-chart>
      <chart-navigator
        v-if="series[0].data.length > 0"
        :selectedPeriod="chartPeriod"
        :chart-index="chartIndex"
        :metrics-length="metricsLength"
        :metrics-period="metricsPeriod"
        @navigateTo="navigateTo"
      />
      <empty-chart v-if="series[0].data.length === 0" />
    </mf-loading>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { QUERY_GET_APP_ACCESSES_BY_HOUR } from '../../graphql'
import { colors } from '@/config/colors.js'
import ChartTypeToggleButton from '@/components/ChartTypeToggleButton.vue'
import ChartNavigator from '@/components/ChartNavigator'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import getAppMetricsNavigatorPeriod from '@/services/getAppMetricsNavigatorPeriod'

export default {
  components: {
    ApexChart: VueApexCharts,
    ChartTypeToggleButton,
    ChartNavigator,
    EmptyChart,
  },
  props: {
    metricsPeriod: String,
  },
  data: () => ({
    chartType: 'bar',
    chartIndex: 0,
    chartRange: [
      '00:00',
      '01:00',
      '02:00',
      '03:00',
      '04:00',
      '05:00',
      '06:00',
      '07:00',
      '08:00',
      '09:00',
      '10:00',
      '11:00',
      '12:00',
      '13:00',
      '14:00',
      '15:00',
      '16:00',
      '17:00',
      '18:00',
      '19:00',
      '20:00',
      '21:00',
      '22:00',
      '23:00',
    ],
  }),
  computed: {
    series() {
      if (!this.appAccessesByHour || this.appAccessesByHour.length === 0) {
        return [{ data: [] }]
      }
      const currentSelectedMetric = [...this.appAccessesByHour[this.chartIndex].metrics]
      const series = this.chartRange.reduce(
        (acc, hour) => {
          const [metric] = currentSelectedMetric.filter((item) => parseInt(item.hour_time) === parseInt(hour))
          if (metric !== undefined) {
            acc[0].data.push(metric.access_count)
          } else {
            acc[0].data.push(0)
          }
          return acc
        },
        [{ data: [] }]
      )
      return series
    },
    metricsLength() {
      if (!this.appAccessesByHour) return 0
      return this.appAccessesByHour.length - 1
    },
    chartPeriod() {
      if (!this.appAccessesByHour || this.appAccessesByHour.length === 0) return
      const currentPeriod = this.metricsPeriod === 'MONTHLY' ? this.appAccessesByHour[this.chartIndex].month : this.appAccessesByHour[this.chartIndex].year
      const { navigatorPeriod } = getAppMetricsNavigatorPeriod(this.metricsPeriod, currentPeriod)
      return navigatorPeriod
    },
    chartCategories() {
      if (!this.appAccessesByHour || this.appAccessesByHour.length === 0) return []
      return this.appAccessesByHour[this.chartIndex].metrics.map((item) => item.age_range)
    },
    chartOptions() {
      return {
        chart: {
          height: 350,
          type: this.chartType,
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
          background: '#fff',
        },
        fill: {
          opacity: 1,
          type: 'solid',
        },
        plotOptions: {
          bar: {
            columnWidth: '30px',
            borderRadius: 3,
            dataLabels: {
              position: 'top',
            },
          },
        },
        grid: {
          padding: {
            top: 10,
            right: 20,
            bottom: 10,
            left: 20,
          },
        },
        colors: [colors[0], colors[1]],
        legend: {
          horizontalAlign: 'left',
          markers: {
            width: 7,
            height: 7,
          },
          offsetX: 5,
          offsetY: -10,
        },
        markers: {
          size: 2,
          colors: [colors[0], colors[1]],
          strokeColors: [colors[0], colors[1]],
          strokeWidth: 2,
          strokeOpacity: 1,
          strokeDashArray: 0,
          fillOpacity: 1,
          shape: 'circle',
          radius: 2,
          showNullDataPoints: true,
          hover: {
            sizeOffset: 2,
          },
        },
        tooltip: {
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const accesses = series[0][dataPointIndex]

            return `
              <v-card class="d-flex flex-row justify-start pa-1">
                <span><strong>${accesses.toLocaleString()} </strong>${accesses > 1 || accesses === 0 ? 'acessos' : 'acesso'}</span>
              </v-card>
            `
          },
        },
        dataLabels: {
          enabled: true,
          position: 'top',
          offsetY: this.chartType === 'bar' ? -20 : -10,
          style: {
            fontSize: '12px',
            colors: ['#F9FAFB'],
            fontWeight: 'normal',
          },
          background: {
            enabled: true,
            foreColor: '#969EA7',
            padding: 4,
            borderRadius: 2,
          },
          formatter(val) {
            const formattedValue = val === null ? '' : val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val
            return formattedValue
          },
        },
        stroke: {
          show: this.chartType !== 'bar',
          curve: 'smooth',
          colors: [colors[0]],
          width: 3,
        },
        xaxis: {
          categories: this.chartRange,
          type: 'category',
          labels: {
            style: {
              colors: '#969EA7',
            },
          },
        },
        yaxis: {
          labels: {
            formatter: function (val, index) {
              return val.toLocaleString()
            },
          },
        },
      }
    },
  },
  apollo: {
    appAccessesByHour: {
      query: QUERY_GET_APP_ACCESSES_BY_HOUR,
      variables() {
        return { metricsPeriod: this.metricsPeriod }
      },
    },
  },
  watch: {
    metricsPeriod() {
      this.chartIndex = 0
    },
  },
  methods: {
    navigateTo(newIndex) {
      this.chartIndex = newIndex
    },
  },
}
</script>

<style lang="scss" scoped>
.card-container {
  min-height: 400px;
}
.local-subtitle {
  width: 80%;
}
</style>
